import { useState } from 'react';
import { useDataGrid } from '@/hooks/useDataGrid';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  useDisclosure,
  Text,
  IconButton,
  Box,
  Flex,
  useBreakpointValue,
} from '@chakra-ui/react';
import { MinusIcon } from '@chakra-ui/icons';
import { FiAlertTriangle } from 'react-icons/fi';
import ModalRetorno from '@/modules/Portaria/components/ModalRetorno';

interface Props<IDType> {
  onConfirm: (id: IDType) => void;
  itemAll?: boolean;
}

export function DeleteButton<IDType = number>({ onConfirm, itemAll }: Props<IDType>) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { queryResult, selecteds } = useDataGrid();
  const [loading, setLoading] = useState<boolean>(false);
  const [mensagemErro, setMensagemErro] = useState<string | null>(null);
  const [mensagemSucesso, setMensagemSucesso] = useState<string | null>(null);

  const alertLeft = useBreakpointValue({ base: '50%', md: '43%' });

  function handleCancel() {
    onClose();
  }

  async function handleConfirm() {
    setLoading(true);
    setMensagemErro(null);
    setMensagemSucesso(null);

    try {
      const results = await Promise.allSettled(
        //@ts-ignore
        selecteds.map((id) => onConfirm(itemAll ? id?.id : id))
      );

      const hasError = results.some((res) => res.status === 'rejected');

      if (hasError) {
        setMensagemErro('Ocorreu um erro ao excluir um ou mais itens.');
      } else {
        setMensagemSucesso('Itens excluídos com sucesso!');
      }

      await queryResult.refetch();
    } catch (e) {
      setMensagemErro('Erro inesperado ao tentar excluir os itens.');
    } finally {
      setLoading(false);
      onClose();
    }
  }

  return (
    <>
      <IconButton
        aria-label="Remove"
        icon={<MinusIcon />}
        onClick={onOpen}
        colorScheme="red"
        ml="auto"
        mr={3}
        size="sm"
        borderRadius="full"
      />

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />

        <Box
          position="absolute"
          top="58px"
          left={alertLeft}
          transform="translate(-50%, -50%)"
          zIndex="2001"
        >
          <Flex
            alignItems="center"
            bg="#117baa"
            borderRadius="30px"
            pl="0"
            pr="20px"
            height="30px"
            boxShadow="md"
          >
            <Box
              as={FiAlertTriangle}
              bg="red.500"
              color="white"
              p="2"
              borderRadius="full"
              w="40px"
              h="40px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="20px"
              mr="2"
            />
            <Text fontWeight="bold" color="white" fontSize="sm">AVISO</Text>
          </Flex>
        </Box>

        <ModalContent borderRadius="3xl" overflow="hidden" mt="60px" >
          <ModalCloseButton />
          <ModalBody bg="gray.50" pt={6}>
            {selecteds.length === 1 ? (
              <Text textAlign="center" fontSize="md" color="gray.700">
                Tem certeza que deseja <strong>excluir este item</strong> permanentemente?
              </Text>
            ) : (
              <Text textAlign="center" fontSize="md" color="gray.700">
                Você está prestes a deletar <strong>{selecteds.length} itens selecionados</strong>.
              </Text>
            )}
          </ModalBody>

          <ModalFooter bg="gray.50" justifyContent="center" gap={3}>
            <Button
              variant="outline"
              onClick={handleCancel}
              borderColor="gray.300"
              _hover={{ bg: 'gray.100' }}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="red"
              isLoading={loading}
              onClick={handleConfirm}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {(mensagemErro || mensagemSucesso) && (
        <ModalRetorno
          isOpen={true}
          onClose={() => {
            setMensagemErro(null);
            setMensagemSucesso(null);
          }}
          mensagem={mensagemErro || mensagemSucesso || ''}
          tipo={mensagemErro ? 'erro' : 'sucesso'}
        />
      )}
    </>
  );
}
